// themeResolved.js — the reactive carrier for the RESOLVED global theme
// (`'dark' | 'light'`), decided in tickets/opus.md ("DECIDED: `themeResolved`
// — the workspace-shell half (`shared/` is approved)", Nika 2026-08-11:
// "shared/ proceeds, split the work").
//
// ── The gap this closes ──────────────────────────────────────────────────
// `root.themeMode` is the user's MODE (`auto|light|dark`); `context.globalTheme`
// is the boot-time mode and is never rewritten. Neither ever carries what
// `'auto'` resolved TO. Under `'auto'`, scratch's own prefers-color-scheme
// listener flips the document's `data-theme` attribute directly — no
// `subscribeTo`/factory anywhere re-fires, so an OS scheme change is
// invisible to the whole tree until something unrelated triggers a repaint
// (`moduleImportBridge.js`'s `moduleTheme()` bridge is the non-reactive
// workaround this gap forced; it stays exactly as-is — this file is
// additive, not a replacement).
//
// ── Why this does NOT duplicate matchMedia ──────────────────────────────
// The rejected shape (see the ticket) is the shell re-deriving the OS
// preference itself — that duplicates logic that belongs a layer down in
// scratch, and only covers whichever surfaces the shell drives directly.
// Instead this watches the EFFECT both causes already produce on `<html>`:
// a `data-theme` attribute mutation. A user-driven `changeGlobalTheme` call
// and scratch's own OS-driven 'auto' flip both write that attribute, so one
// MutationObserver singleton, filtered to `attributeFilter: ['data-theme']`,
// catches every cause with zero framework hook and zero matchMedia
// duplication — the exact pattern `watchCanvasBackgroundTheme` (below,
// canvasBackground.js) already proved out for the canvas-bg cookie var.
import { resolveCanvasBackgroundTheme } from './canvasBackground.js'

// Read the CURRENT resolved theme without attaching anything. `mode` (if
// passed) lets an explicit `'light'|'dark'` win over whatever `data-theme`
// happens to read right now (useful for the boot-time seed, where the
// persisted mode is already known and authoritative). Falls through to the
// `data-theme` attribute, then `prefers-color-scheme` — identical resolution
// order to `resolveCanvasBackgroundTheme`, reused rather than re-implemented.
export const resolveThemeResolvedNow = (doc = globalThis.document, mode) =>
  resolveCanvasBackgroundTheme(mode, doc)

// Singleton per document: seeds `root.themeResolved` immediately and
// re-writes it on every future `data-theme` mutation — user-driven
// (`changeGlobalTheme`) AND OS-driven (scratch's own `'auto'`
// prefers-color-scheme listener). Idempotent; safe to call more than once
// per document (e.g. bootShell's warm-start + cold paths both landing on
// the same live root) — a second call is a no-op past the seed write.
//
// `root` is the LIVE reactive root state (`shell.state.root` /
// `element.state.root` — the same object every existing consumer reads via
// `s.root`), so a bare `root.themeResolved = next` assignment is reactive by
// the same mechanism `cycleTheme.js`'s `root.themeMode = next` already
// relies on. Returns the MutationObserver (or null off-document / no
// MutationObserver global, e.g. SSR/tests without a DOM shim).
export const watchThemeResolved = (root, doc = globalThis.document, mode) => {
  const rootEl = doc?.documentElement
  if (!rootEl || !root) return null

  // Inlined resolveThemeResolvedNow's own body rather than calling it bare —
  // a bare sibling-export call is the same frank-trap class this sweep
  // exists to fix (project_frank_drops_module_local_helpers).
  // resolveCanvasBackgroundTheme stays a bare reference: it is an IMPORT
  // (canvasBackground.js), not a module-local/sibling-export binding, and
  // imports resolve fine post-frank.toJSON. resolveThemeResolvedNow itself
  // stays exported — bootShell.js imports and calls it directly.
  root.themeResolved = resolveCanvasBackgroundTheme(mode, doc)

  if (doc.__themeResolvedWatcher) return doc.__themeResolvedWatcher

  const Observer = doc.defaultView?.MutationObserver || globalThis.MutationObserver
  if (!Observer) return null

  const observer = new Observer(() => {
    root.themeResolved = resolveCanvasBackgroundTheme(undefined, doc)
  })
  observer.observe(rootEl, { attributes: true, attributeFilter: ['data-theme'] })
  doc.__themeResolvedWatcher = observer
  return observer
}
